import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import type { FizboListing } from '@/hooks/useFizbo';

export interface DashboardTask {
  id: string;
  task: string;
  time: string;
  done: boolean;
  urgent: boolean;
}

export interface DashboardStats {
  totalToday: number;
  totalActivePortfolios: number;
  pendingReturns: number;
  todayCalls: number;
  conversionRate: number;
  isLoading: boolean;
  error: string | null;
}

function todayStr(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' });
}

function mapDbToFizbo(row: Record<string, unknown>): FizboListing {
  return {
    id: row.id as string,
    listingNo: row.listing_no as string,
    ownerName: row.owner_name as string,
    phone: row.phone as string,
    propertyType: row.property_type as string,
    transactionType: row.transaction_type as string,
    location: row.location as string,
    status: row.status as string,
    daysOld: row.days_old as number,
    fatigueScore: row.fatigue_score as number,
    kanbanStage: (row.kanban_stage as string) || 'Yeni',
    price: Number(row.price) || 0,
    sqm: row.sqm as number | null,
    sellabilityScore: (row.sellability_score as number) || 50,
    notes: (row.notes as string) || '',
    priceHistory: Array.isArray(row.price_history) ? row.price_history : [],
    createdAt: row.created_at ? (row.created_at as string).split('T')[0] : '',
  };
}

export function useDashboardStats() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalToday: 0,
    totalActivePortfolios: 0,
    pendingReturns: 0,
    todayCalls: 0,
    conversionRate: 0,
    isLoading: true,
    error: null,
  });
  const [recentListings, setRecentListings] = useState<FizboListing[]>([]);
  const [todayTasks, setTodayTasks] = useState<DashboardTask[]>([]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    const today = todayStr();

    const [r1, r2, r3, r4, r5, r6] = await Promise.all([
      // Bugün eklenen FİZBO
      supabase
        .from('ep_fizbo_listings')
        .select('id')
        .eq('user_id', user.id)
        .gte('created_at', `${today}T00:00:00`)
        .lte('created_at', `${today}T23:59:59`),

      // Aktif portföy sayısı
      supabase
        .from('ep_portfolios')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'Aktif'),

      // Bugün acil bekleyen görevler (dönüş)
      supabase
        .from('ep_reminders')
        .select('id')
        .eq('user_id', user.id)
        .eq('due_date', today)
        .eq('is_done', false)
        .eq('is_urgent', true),

      // Bugün tamamlanan görevler
      supabase
        .from('ep_reminders')
        .select('id')
        .eq('user_id', user.id)
        .eq('due_date', today)
        .eq('is_done', true),

      // Tüm FİZBO ve Kabul sayısı (dönüşüm oranı için)
      supabase
        .from('ep_fizbo_listings')
        .select('status')
        .eq('user_id', user.id),

      // Son 5 ilan + bugünkü görevler (parallel)
      Promise.all([
        supabase
          .from('ep_fizbo_listings')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('ep_reminders')
          .select('*')
          .eq('user_id', user.id)
          .eq('due_date', today)
          .order('due_time', { ascending: true }),
      ]),
    ]);

    const errors = [r1.error, r2.error, r3.error, r4.error, r5.error].filter(Boolean);
    if (errors.length > 0) {
      setStats((prev) => ({
        ...prev,
        error: (errors[0] as Error).message || 'Veri yüklenirken hata oluştu',
        isLoading: false,
      }));
      return;
    }

    const todayFizbo = (r1.data || []).length;
    const activePortfolios = (r2.data || []).length;
    const pending = (r3.data || []).length;
    const completed = (r4.data || []).length;

    const allFizbo = r5.data || [];
    const total = allFizbo.length;
    const kabul = allFizbo.filter((f: { status?: string }) => f.status === 'Kabul').length;
    const conversionRate = total > 0 ? Math.round((kabul / total) * 100) : 0;

    setStats({
      totalToday: todayFizbo,
      totalActivePortfolios: activePortfolios,
      pendingReturns: pending,
      todayCalls: completed,
      conversionRate,
      isLoading: false,
      error: null,
    });

    const [listingsData, tasksData] = r6;
    setRecentListings((listingsData.data || []).map(mapDbToFizbo));
    setTodayTasks(
      (tasksData.data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        task: row.task as string,
        time: (row.due_time as string) || '',
        done: row.is_done as boolean,
        urgent: row.is_urgent as boolean,
      }))
    );
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const toggleTask = useCallback(
    async (taskId: string, done: boolean) => {
      if (!user) return;
      const { error } = await supabase
        .from('ep_reminders')
        .update({ is_done: !done })
        .eq('id', taskId)
        .eq('user_id', user.id);

      if (!error) {
        setTodayTasks((prev) =>
          prev.map((t) => (t.id === taskId ? { ...t, done: !done } : t))
        );
        // Tamamlanan sayısını güncelle
        setStats((prev) => ({
          ...prev,
          todayCalls: !done ? prev.todayCalls + 1 : Math.max(0, prev.todayCalls - 1),
          pendingReturns: done ? prev.pendingReturns + 1 : Math.max(0, prev.pendingReturns - 1),
        }));
      }
    },
    [user]
  );

  return { stats, recentListings, todayTasks, toggleTask, refresh: fetchStats };
}